import { FC, useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash';

import { useBreakpoint } from '@contexts/breakpoints';
import { callService } from '@api/caller';
import { SearchViewModel } from '@api/index';
import { FlexibleDiv } from '@components/formStyles';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import {
  ItemText,
  ItemTextCity,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon from '@components/utils/CustomIcon';
import makeBold from '@utils/makeBold';

import {
  AntAutoComplete,
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  ItemErrorText,
  Label,
  OptionWrapper,
  SelectWrapper,
} from './styled';

export type HotelTypes = SearchViewModel | null;

type PropsTypes = {
  label: string;
  onChange?: (data: HotelTypes) => void;
  isSubmit?: boolean;
  resetIsSubmit: () => void;
};

const { Option } = AntAutoComplete;

const HotelAutoComplete: FC<PropsTypes> = ({
  label,
  onChange: pOnChange = (v) => v,
  isSubmit,
  resetIsSubmit,
}) => {
  const { xs } = useBreakpoint();

  const [value, setValue] = useState<HotelTypes>(null);
  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState<Array<SearchViewModel> | []>([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isHotelResultsLoading, setIsHotelResultsLoading] = useState(false);
  const [isHotelDataFetched, setIsHotelDataFetched] = useState(false);

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (value === '') {
      setValue(null);
      pOnChange(null);
    }

    setIsHotelDataFetched(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsHotelResultsLoading(true);
      const hotels = await callService('SearchesService', 'searchesHotel', {
        s: s,
      });
      setData(hotels);
    } catch (error) {
      setData([]);
    } finally {
      setIsHotelResultsLoading(false);
      setIsHotelDataFetched(true);
    }
  };

  const searchHotel = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue = value?.text !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchHotel(inputValue);
    } else {
      setData([]);
    }
  }, [inputValue]);

  useEffect(() => {
    if (value) {
      pOnChange(value);
      setIsError(false);
    } else {
      pOnChange(null);
      setIsError(true);
    }
  }, [value]);

  const handleFocus = () => {
    setIsFocus(true);
    setIsTouched(true);
  };

  const handleBlur = () => {
    setIsFocus(false);
    setIsTouched(false);
    resetIsSubmit();
  };

  useEffect(() => {
    if (isSubmit) {
      setIsTouched(true);
    }
  }, [isSubmit]);

  const selectValue = (option: SearchViewModel) => {
    setValue(option);
    pOnChange(option);
    setInputValue(option?.text || '');
  };

  const isShowItemTextError =
    isHotelDataFetched &&
    !isHotelResultsLoading &&
    inputValue.length > 2 &&
    data &&
    Array.isArray(data) &&
    !data.length;

  return (
    <SelectWrapper>
      <Label>{label}</Label>
      <AntAutoComplete
        onSearch={handleSearch}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={<div style={{ marginTop: 25 }}>Otel veya bölge adı</div>}
        allowClear
        isFocus={isFocus}
        isError={(isError && isTouched) as boolean}
        value={inputValue}
        dropdownStyle={{
          minWidth: xs || xs !== undefined ? 'auto' : '400px',
        }}
      >
        {data && Array.isArray(data) && data.length ? (
          data.map((option) => (
            <Option key={`${option.id}-${option.text}`}>
              <OptionWrapper onClick={() => selectValue(option)}>
                <CustomIcon
                  width={24}
                  height={24}
                  fontSize={24}
                  paletteColor='primaryBrand'
                  icon={getLocationIcon(option?.type || '')}
                />
                <FlexibleDiv right={17} />
                <ItemTextWrapper>
                  <ItemText
                    dangerouslySetInnerHTML={{
                      __html: makeBold(option.text || '', inputValue),
                    }}
                  />
                  {option?.city || option?.country ? (
                    <ItemTextCity>
                      {option?.city
                        ? option?.country
                          ? `${option?.city}, ${option?.country}`
                          : `${option?.city}`
                        : option?.country
                        ? `${option?.country}`
                        : ''}
                    </ItemTextCity>
                  ) : null}
                </ItemTextWrapper>
              </OptionWrapper>
            </Option>
          ))
        ) : isShowItemTextError ? (
          <Option>
            <OptionWrapper>
              <ItemErrorText>
                {!xs || xs === undefined ? (
                  <span>Aradığınız kriterlere uygun sonuç bulunamadı.</span>
                ) : (
                  <span>
                    Aradığınız kriterlere uygun <br />
                    sonuç bulunamadı.
                  </span>
                )}
              </ItemErrorText>
            </OptionWrapper>
          </Option>
        ) : null}
      </AntAutoComplete>
      {!isFocus && isTouched && isError && (
        <ErrorMessageContainer>
          <Arrow />
          <ErrorMessage>Şehir yada otel adı seçiniz</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </SelectWrapper>
  );
};

export default HotelAutoComplete;
